// Theme Configuration File
// Customize colors, fonts, and text from this central file

export const theme = {
  // Color Palette
  colors: {
    // Primary Colors (Burgundy/Maroon)
    primary: {
      50: '#fef2f3',
      100: '#fde6e8',
      200: '#fbd0d5',
      300: '#f7aab2',
      400: '#f17a88',
      500: '#e54d61',
      600: '#d1354a',
      700: '#a52a3a',
      800: '#8b2332',
      900: '#6b1a27',
      radio: {
        selected: '#3b82f6',
      },
    },
    
    // Secondary Colors (Teal Accent)
    secondary: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
    },
    
    // Accent Colors
    accent: {
      teal: '#2C7A7B',
      beige: '#D4B896',
      lightBeige: '#E8DCC8',
      darkTeal: '#1F5456',
    },
    
    // Semantic Colors
    semantic: {
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
    
    // Background Colors
    background: {
      primary: '#ffffff',
      secondary: '#F7F7F7',
      tertiary: '#E5E5E5',
      light: '#FAFAFA',
    },
    
    // Text Colors
    text: {
      primary: '#2D3748',
      secondary: '#4A5568',
      tertiary: '#718096',
      inverse: '#ffffff',
      light: '#A0AEC0',
    },
  },
  
  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", system-ui, sans-serif',
      secondary: '"Roboto", system-ui, sans-serif',
      mono: '"JetBrains Mono", monospace',
    },
    
    fontSize: {
      xs: '0.75rem',      // 12px
      sm: '0.875rem',     // 14px
      base: '1rem',       // 16px
      lg: '1.125rem',     // 18px
      xl: '1.25rem',      // 20px
      '2xl': '1.5rem',    // 24px
      '3xl': '1.875rem',  // 30px
      '4xl': '2.25rem',   // 36px
      '5xl': '3rem',      // 48px
      '6xl': '3.75rem',   // 60px
    },
    
    fontWeight: {
      light: '300',
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
    },
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },
  
  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Component-specific theme configurations
export const componentThemes = {
  // Navigation Bar
  navbar: {
    backgroundColor: theme.colors.primary[800],
    textColor: theme.colors.text.inverse,
    logoSize: 'h-10 w-10',
    linkHoverBg: theme.colors.primary[700],
    linkActiveBg: theme.colors.primary[900],
  },
  
  // Buttons
  buttons: {
    primary: {
      backgroundColor: theme.colors.primary[800],
      hoverBackgroundColor: theme.colors.primary[900],
      textColor: theme.colors.text.inverse,
    },
    secondary: {
      backgroundColor: theme.colors.secondary[600],
      hoverBackgroundColor: theme.colors.secondary[700],
      textColor: theme.colors.text.inverse,
    },
    success: {
      backgroundColor: theme.colors.semantic.success,
      hoverBackgroundColor: '#059669',
      textColor: theme.colors.text.inverse,
    },
    danger: {
      backgroundColor: theme.colors.semantic.error,
      hoverBackgroundColor: '#dc2626',
      textColor: theme.colors.text.inverse,
    },
  },
  
  // Cards
  cards: {
    backgroundColor: theme.colors.background.primary,
    borderColor: theme.colors.secondary[200],
    shadow: theme.shadows.base,
    hoverShadow: theme.shadows.lg,
    hoverScale: 'scale-105',
  },
  
  // Forms
  forms: {
    inputBorderColor: theme.colors.secondary[300],
    inputFocusBorderColor: theme.colors.primary[500],
    inputFocusRingColor: theme.colors.primary[500],
    labelColor: theme.colors.text.secondary,
  },
  
  // Date Picker
  datePicker: {
    selectedBgColor: theme.colors.primary[600],
    selectedTextColor: theme.colors.text.inverse,
    inRangeBgColor: theme.colors.primary[100],
    inRangeTextColor: theme.colors.primary[800],
    todayBgColor: theme.colors.secondary[100],
    todayTextColor: theme.colors.text.primary,
  },
};

// Layout configurations
export const layout = {
  container: {
    maxWidth: '7xl',
    padding: {
      xs: 'px-4',
      sm: 'px-6',
      lg: 'px-8',
    },
  },
  
  navbar: {
    height: 'h-16',
  },
  
  sidebar: {
    width: 'w-64',
    backgroundColor: theme.colors.secondary[900],
  },
};

// Animation configurations
export const animations = {
  transitions: {
    fast: 'transition-colors duration-150',
    base: 'transition-colors duration-200',
    slow: 'transition-colors duration-300',
  },
  
  transforms: {
    hover: 'hover:scale-105',
    active: 'active:scale-95',
  },
  
  spin: 'animate-spin',
};

// Responsive breakpoints
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Utility functions for theme access
export const getColor = (colorPath: string) => {
  const keys = colorPath.split('.');
  let value: any = theme.colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${colorPath}" not found in theme`);
      return '#000000';
    }
  }
  
  return value;
};

export const getTypography = (path: string) => {
  const keys = path.split('.');
  let value: any = theme.typography;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Typography path "${path}" not found in theme`);
      return '';
    }
  }
  
  return value;
};

export default theme;
